import * as colors from '@radix-ui/colors';
import { createGlobalStyle, css } from 'styled-components';

// clerk overrides general css styles
const clerkFontFamilyCss = css`
  font-family: 'DM Sans';
  font-style: normal;
`;

const clerkBoldFontCss = css`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.4px;
`;

const clerkBaseFontCss = css`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.4px;
`;

const clerkThinFontCss = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.42px;
`;

const clerkSmallFontCss = css`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.48px;
`;

const clerkSmallThickFontCss = css`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.48px;
`;

const clerkBigFontCss = css`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.32px;
`;

const clerkErrorCss = css`
  text-align: left;
  color: rgba(205, 0, 8, 0.76);
  ${clerkSmallFontCss}
`;

const clerkBlackButtonBaseCss = css`
  border-radius: 9999px;
  background: #1c2024;
  display: flex;
  height: 40px;
  padding: 0px ${props => props.theme.spacing[4]};
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  align-self: stretch;
`;

const clerkLightButtonSmallCss = css`
  border-radius: 9999px;
  display: flex;
  height: 32px;
  padding: 0px ${props => props.theme.spacing[3]};
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  align-self: stretch;
  text-transform: none;
  color: ${props => props.theme.color.neutral[11]};
  :hover {
    background-color: ${props => props.theme.color.neutralAlpha[3]};
  }
  :active {
    background-color: ${props => props.theme.color.neutralAlpha[4]};
  }
`;

const clerkRadixGhostButtonSmallCss = css`
  border-radius: 9999px;
  background: ${props => props.theme.color.neutral[1]};
  display: flex;
  height: 32px;
  padding: 0px ${props => props.theme.spacing[3]};
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  align-self: stretch;
  text-transform: none;
  border: 1px solid ${props => props.theme.color.neutralAlpha[11]};
  color: ${props => props.theme.color.neutral[12]};
  font-weight: 700;
  :hover {
    background-color: ${props => props.theme.color.neutralAlpha[2]};
  }
  :active {
    background-color: ${props => props.theme.color.neutralAlpha[3]};
  }
  :disabled {
    border: 1px solid ${props => props.theme.color.neutralAlpha[7]};
    color: ${props => props.theme.color.neutralAlpha[8]};
  }
`;

const clerkFactorStepsBaseCss = css`
  gap: ${props => props.theme.spacing[7]};
  .cl-identityPreview {
    justify-content: unset;
    p {
      ${clerkFontFamilyCss}
      color: rgba(0, 7, 19, 0.62);
      ${clerkBoldFontCss}
    }
  }
  .cl-form {
    gap: 0px;
  }
  .cl-formHeaderTitle {
    text-align: left;
    color: #1c2024;
    ${clerkBoldFontCss}
    margin-top: 9px;
  }
  .cl-formHeaderSubtitle {
    text-align: left;
    color: #60646c;
    ${clerkThinFontCss}
  }
  .cl-otpCodeFieldInputs {
    width: 100%;
    justify-content: space-between;
  }
  .cl-otpCodeFieldInput {
    width: 48px;
    max-width: unset;
    color: #1c2024;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
  }
  .cl-otpCodeFieldErrorText {
    color: rgba(205, 0, 8, 0.76);
    ${clerkSmallFontCss}
    svg {
      display: none;
    }
  }
  .cl-internal-1s2rqwh {
    width: 100%;
  }
  .cl-formResendCodeLink {
    width: 100%;
    text-align: center;
    color: #60646c;
    ${clerkBoldFontCss}
    margin: 0px;
  }
`;

const clerkBackgroundImageCss = css`
  background-repeat: no-repeat;
  width: 16px;
  height: 16px;
`;

const GlobalStyles = createGlobalStyle`
  /****** IMPORTED FONTS ******/
  /* =======================================================
     DM Sans
     =======================================================
     2025 - 🤷
     https://github.com/googlefonts/dm-fonts/tree/main/Sans/fonts/webfonts
     ======================================================= */

  @import url('https://cdn.growth.onomondo.com/fonts/dm_sans_normal_400.woff2');
  @import url('https://cdn.growth.onomondo.com/fonts/dm_sans_normal_700.woff2');

  @font-face {
    font-family: 'DM Sans';
    src: url('https://cdn.growth.onomondo.com/fonts/dm_sans_normal_400.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
  }

  @font-face {
    font-family: 'DM Sans';
    src: url('https://cdn.growth.onomondo.com/fonts/dm_sans_normal_700.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
  }

  /* ==========================================================================
    GLOBAL STYLES
    ========================================================================== */

  /* Box Sizing
    ========================================================================== */
  html {
    box-sizing: border-box;
    height: 100%;
    width: 100%;
  }

  body {
    margin: 0;
    padding: 0;
    line-height: 1.6;
    background-color: var(--neutral-contrast);
  }

  body {
    font-family: "DM Sans", serif;
  }

  button, input, textarea {
    font-family: inherit;
  }

  input::placeholder {
    font-family: inherit;
  }

  #root {
    padding: 0;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* PARAGRAPHS
    ========================================================================== */

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    margin: 0;
    hyphens: auto;
  }

  /* HEADINGS
    ========================================================================== */

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }

  h1 {
    font-weight: 700;
    font-size: 28px;
    line-height: 34px;
  }

  h2 {
    color: ${props => props.theme.dark};
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
  }

  h3 {
    color: ${props => props.theme.copperDarkest};
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
  }

  h4 {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    color: ${props => props.theme.darker};
  }

  /* LINKS
    ========================================================================== */

  a {
    text-decoration: none;
    color: ${props => props.theme.link.color};
  }
  a[data-underline] {
    text-decoration: underline;
  }

  /* A better looking default horizontal rule
    ========================================================================== */

  hr {
    display: block;
    height: 1px;
    border: 0;
    border-top: 1px solid #ccc;
    margin: 1em 0;
    padding: 0;
  }

  /*
  * Remove the gap between audio, canvas, iframes,
  * images, videos and the bottom of their containers:
  * https://github.com/h5bp/html5-boilerplate/issues/440
  ========================================================================== */

  img,
  svg {
    vertical-align: middle;
  }

  /* IMAGES
    ========================================================================== */

  img {
    height: auto;
    width: auto;
    /* max-width allows image to be responsive. */
    max-width: 100%;
  }

  /* LISTS
    ========================================================================== */
  ul {
    padding-left: ${props => props.theme.spacing[5]};
  }

  /* mapboxgl overrides */
  .mapboxgl-popup-close-button {
    color: ${colors.slate.slate12};
  }

  /* clerk overrides */

  .cl-page p {
    color: ${props => props.theme.color.neutral[11]};
  }
  .cl-page button {
    color: ${props => props.theme.color.accent[11]};
  }
  .cl-rootBox {
    ${clerkFontFamilyCss}
  }
  .cl-card {
    min-width: 344px;
    box-shadow: none;
    margin: 0px 17px;
    padding: 28.5px 2px 36px;
    @media (max-width: 1025px) {
      min-width: 0px;
    }
  }

  .cl-header {
    margin: ${props => props.theme.spacing[5]} 0;
    gap: ${props => props.theme.spacing[2]};
  }

  .cl-headerTitle {
    color: var(--neutral-12);
    text-align: center;
    font-size: 24px;
    font-weight: 500;
    line-height: 30px;
  }

  .cl-headerSubtitle {
    text-align: center;
    color: ${props => props.theme.color.neutral[11]};
    ${clerkThinFontCss}
  }

  .cl-headerBackRow {
    justify-content: center;
    a {
      gap: ${props => props.theme.spacing[2]};
      svg {
        height: 16px;
        width: 16px;
        color: #60646C;
      }
    }
  }

  .cl-headerBackLink {
    color: #60646C;
    ${clerkBoldFontCss}
  }

  .cl-alternativeMethods {
    gap: ${props => props.theme.spacing[3]};
  }

  .cl-socialButtons {
    gap: ${props => props.theme.spacing[3]};
  }

  .cl-alternativeMethodsBlockButton {
    ${clerkBlackButtonBaseCss}
    :hover {
      border-radius: 9999px;
      background: #1C2024;
      svg {
        width: 0px;
        height: 0px;
        stroke: #1C2024;
      }
    }
    svg {
      width: 0px;
      height: 0px;
    }
  }

  .cl-alternativeMethodsBlockButtonText {
    color: #FCFCFD;
    ${clerkBigFontCss}
  }

  .cl-internal-egu47 {
    justify-content: center;
  }

  .cl-identityPreview {
    height: 32px;
    max-width: unset;
    justify-content: space-between;
    gap: ${props => props.theme.spacing[2]};
    .cl-identityPreviewText {
      ${clerkFontFamilyCss}
      color: rgba(0, 7, 19, 0.62);
      ${clerkBoldFontCss}
    }
    div {
      margin-right: 0px;
    }
    p {
      margin-right: 0px;
    }
    button {
      margin-left: auto;
      padding: 0px;
      min-height: unset;
      svg {
        display: none;
      }
      background-image: url(${require('~/assets/graphics/edit-05.svg')});
      ${clerkBackgroundImageCss}
    }
  }

  .cl-signIn-forgotPasswordMethods {
    .cl-identityPreview {
        justify-content: unset;
    }
    .cl-internal-uqm3h2 {
      .cl-alternativeMethodsBlockButton {
        border-radius: 9999px;
        border: 1px solid rgba(1, 6, 47, 0.17);
        justify-content: center;
        gap: ${props => props.theme.spacing[2]};
        background: #ffffff;
        div {
          width: unset;
        }
        :hover {
          background: rgba(0, 0, 0, 0.04);
          svg {
            width: 0px;
            height: 0px;
            stroke: #1C2024;
          }
        }
      }
      .cl-alternativeMethodsBlockButtonText {
        color: #60646C;
        ${clerkBoldFontCss}
      }
    }
  }

  .cl-signIn-totp2Fa {
    ${clerkFactorStepsBaseCss}
    .cl-otpCodeField {
      width: 100%;
      align-items: center;
      margin-top: ${props => props.theme.spacing[4]};
      margin-bottom: 0px;
    }
    svg {
      background-image: url(${require('~/assets/graphics/shield-tick.svg')});
      ${clerkBackgroundImageCss}
      path {
        display: none;
      }
    }
  }

  .cl-signIn-emailLinkVerify {
    ${clerkFactorStepsBaseCss}
    .cl-formHeaderTitle {
      text-align: center;
    }
    .cl-formHeaderSubtitle {
      text-align: center;
    }
    .cl-formResendCodeLink {
      margin-top: ${props => props.theme.spacing[4]};
    }
  }

  .cl-signIn-resetPassword {
    ${clerkFactorStepsBaseCss}
    .cl-otpCodeField {
      width: 100%;
      align-items: center;
      margin-top: ${props => props.theme.spacing[4]};
      margin-bottom: ${props => props.theme.spacing[7]};
    }
    .cl-formResendCodeLink {
      width: 100%;
      text-align: center;
      color: #60646C;
      ${clerkBoldFontCss}
      margin: 0px;
    }
  }

  .cl-signIn-havingTrouble {
    .cl-main {
      button {
        ${clerkBlackButtonBaseCss}
        div {
          span {
            color: #FCFCFD;
            ${clerkBigFontCss}
          }
        }
        svg {
          width: 0px;
          height: 0px;
          stroke: #1C2024;
        }
      }
    }
  }

  .cl-avatarBox {
    background-image: url(${require('~/assets/graphics/user-03.svg')});
    ${clerkBackgroundImageCss}
    background-color: unset;
    img {
      display:none;
    }
  }

  .cl-socialButtonsBlockButtonText {
    color: #60646C;
    ${clerkBoldFontCss}
  }

  .cl-socialButtonsBlockButton {
    border-radius: 9999px;
    border: 1px solid rgba(1, 6, 47, 0.17);
    justify-content: center;
    gap: ${props => props.theme.spacing[2]};
    div {
      width: unset;
    }
  }

  .cl-main {
    gap: ${props => props.theme.spacing[7]};
    p {
      color: #60646C;
      text-align: center;
      ${clerkSmallFontCss}
    }
  }

  .cl-formButtonPrimary {
    ${clerkBlackButtonBaseCss}
    margin-top: 24px;
    color: #FCFCFD;
    ${clerkBigFontCss}
    text-transform: none;
  }

  .cl-form {
    gap: ${props => props.theme.spacing[4]};
  }

  .cl-formField {
    gap: ${props => props.theme.spacing[1]};
    display: flex;
  }

  .cl-formFieldLabel {
    color: var(--neutral-12);
    ${clerkBaseFontCss}
  }

  .cl-formFieldInput {
    border-color: var(--neutral-a7);
    background-color: var(--neutral-1);
    color: var(--neutral-12);
    ${clerkThinFontCss}
    :focus {
      box-shadow: none;
    }
  }

  .cl-formFieldInputGroup {
    .cl-formFieldInputShowPasswordButton {
      box-shadow: none;
      svg {
        path {
          fill: ${props => props.theme.color.neutralAlpha[8]};
        }
      }
    }
  }

  .cl-formFieldAction {
    color: ${props => props.theme.copper};
    ${clerkSmallThickFontCss}
  }

  .cl-formFieldErrorText {
    svg {
      display:none;
    }
  }

  #error-password {
    ${clerkErrorCss}
  }

  #error-confirmPassword {
    ${clerkErrorCss}
  }

  #error-identifier {
    ${clerkErrorCss}
  }

  .cl-alert {
    display: flex;
    width: 100%;
    padding: ${props => props.theme.spacing[3]};
    align-items: flex-start;
    gap: ${props => props.theme.spacing[2]};
    border-radius: 8px;
    border: 1px solid #F9C6C6;
    background: #FFF7F7;
    align-items: center;

    .cl-alertText {
      color: rgba(187, 0, 7, 0.84);
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.4px;
    }
    svg {
      margin-right: 0px;
      background-image: url(${require('~/assets/graphics/info-circle-red.svg')});
      ${clerkBackgroundImageCss}
      path {
        display: none;
      }
    }
  }

  .cl-footer {
    justify-content: center;
  }

  .cl-footerAction__signIn {
    margin-top: ${props => props.theme.spacing[5]};
    .cl-footerActionLink {
      color: var(--orange-9);
      ${clerkBoldFontCss}
    }
  }

  .cl-footerActionText {
    color: #1C2024;
    ${clerkThinFontCss}
  }

  .cl-footerActionLink {
    color: #60646C;
    ${clerkBoldFontCss}
  }

  .cl-main ~ div[class^='cl-internal'] {
    display: none;
  }

  /* here is styling for the profile settings modal */

  .cl-modalBackdrop {
    background-color: rgba(0, 0, 0, 0.4);
  }
  .cl-userProfile-root {
    .cl-card {
      background-color: ${props => props.theme.color.neutral[2]};
      box-shadow: 0 0 0 1px ${props => props.theme.color.neutral[4]};
      padding-top: 68px;
      padding-bottom: ${props => props.theme.spacing[5]};
      div:has(> .cl-formButtonPrimary), div:has(> .cl-formButtonReset) {
        position: absolute;
        right: 24px;
        bottom: 0px;
        width: 100%;
        margin-top: 0px;
      }
    }

    .cl-modalCloseButton {
      margin: ${props => props.theme.spacing[5]} ${props => props.theme.spacing[5]} 0px 0px;
      padding: 3px;
      min-height: ${props => props.theme.spacing[4]};
      max-height: ${props => props.theme.spacing[4]};
      svg {
        height: 10px;
        width: 10px;
        path {
          stroke: ${props => props.theme.color.neutral[12]};
        }
      }
    }

    .cl-navbar, .cl-pageScrollBox {
      padding-top: 0;
      padding-bottom: 0;
      padding-left: ${props => props.theme.spacing[5]};
      padding-right: ${props => props.theme.spacing[5]};
    }

    .cl-navbar {
      max-width: 229px;
      border-right: 1px solid ${props => props.theme.color.neutralAlpha[4]};
      .cl-navbarButtons {
        gap: ${props => props.theme.spacing[2]};
        width: 180px;
        .cl-navbarButton {
          gap: ${props => props.theme.spacing[2]};
          padding: 0px ${props => props.theme.spacing[3]};
          height: ${props => props.theme.spacing[6]};
          ${clerkFontFamilyCss}
          color: ${props => props.theme.color.neutral[11]};
          ${clerkBoldFontCss}
          opacity: 1;
          :focus {
            box-shadow: none;
          }
        }
      }
    }

    .cl-navbarButton__account, .cl-navbarButton__security, .cl-formFieldInputCopyToClipboardButton {
      &.cl-active {
        background-color: ${props => props.theme.color.neutralAlpha[4]};
      }
      :hover {
        background-color: ${props => props.theme.color.neutralAlpha[3]};
      }
      svg {
        height: ${props => props.theme.spacing[4]};
        width: ${props => props.theme.spacing[4]};
        path {
          display: none;
        }
      }
    }

    .cl-navbarButton__account {
      svg {
        background-image: url(${require('~/assets/graphics/user-03.svg')});
      }
    }

    .cl-navbarButton__security {
      svg {
        background-image: url(${require('~/assets/graphics/shield-tick.svg')});
      }
    }

    .cl-profileSectionPrimaryButton__password {
      span {
        svg {
          background-image: url(${require('~/assets/graphics/edit-02.svg')});
        }
      }
    }

    .cl-profileSectionPrimaryButton__mfa {
      span {
        svg {
          background-image: url(${require('~/assets/graphics/plus.svg')});
        }
      }
    }

    .cl-scrollBox {
      .cl-pageScrollBox {
        margin-bottom: ${props => props.theme.spacing[7]};
        /* the following is selecting the right side of the profile settings modal main page */
        .cl-page {
          gap: ${props => props.theme.spacing[7]};
          .cl-accordionTriggerButton:has(+ div) {
            background-color: ${props => props.theme.color.neutralAlpha[2]};
          }
          .cl-profilePage__account {
            margin-bottom: ${props => props.theme.spacing[4]};
            .cl-userPreview {
              gap: ${props => props.theme.spacing[2]};
            }
          }
          .cl-profilePage__security {
            .cl-profileSectionContent__password {
              p {
                padding: ${props => props.theme.spacing[1]} 0px;
                color: ${props => props.theme.color.neutral[12]};
              }
            }
            .cl-accordionContent {
              padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
              div {
                gap: ${props => props.theme.spacing[1]};
                div {
                  gap: 0px;
                  button {
                    margin-top: ${props => props.theme.spacing[1]};
                    color: ${props => props.theme.color.neutral[11]};
                    ${clerkSmallThickFontCss}
                    :focus {
                      box-shadow: none;
                    }
                  }
                  div {
                    div {
                      p {
                        color: ${props => props.theme.color.neutral[12]};
                        ${clerkSmallThickFontCss}
                      }
                    }
                    p {
                      color: ${props => props.theme.color.neutral[11]};
                      ${clerkSmallFontCss}
                    }
                  }
                }
              }
            }
            .cl-profileSectionContent__mfa {
              .cl-accordionTriggerButton {
                div {
                  span:nth-child(1) {
                    color: ${props => props.theme.color.neutral[12]};
                    ${clerkBoldFontCss}
                  }
                }
                span {
                  svg {
                    height: 16px;
                    width: 16px;
                    path {
                      display: none;
                    }
                  }
                }
                svg {
                  stroke: ${props => props.theme.color.neutral[12]};
                }
              }
              div:nth-child(1) {
                .cl-accordionTriggerButton {
                  span {
                    svg {
                      background-image: url(${require('~/assets/graphics/fingerprint-01.svg')});
                    }
                  }
                }
                button[data-localization-key='userProfile.start.mfaSection.totp.destructiveActionLabel'] {
                  color: ${props => props.theme.color.error[11]};
                  :focus {
                    box-shadow: none;
                  }
                }
              }
              div:nth-child(2) {
                .cl-accordionTriggerButton {
                  span {
                    svg {
                      background-image: url(${require('~/assets/graphics/passcode.svg')});
                    }
                  }
                }
              }
            }
            .cl-profileSectionContent__activeDevices {
              .cl-activeDevice {
                gap: ${props => props.theme.spacing[4]};
                div {
                  gap: 0px;
                  div {
                    p {
                      margin-right: ${props => props.theme.spacing[2]};
                      color: ${props => props.theme.color.neutral[12]};
                      ${clerkBoldFontCss}
                    }
                  }
                  p {
                    color: ${props => props.theme.color.neutral[11]};
                    ${clerkThinFontCss}
                  }
                }
              }
              .cl-accordionTriggerButton {
                border: 1px solid transparent;
                :focus {
                  box-shadow: none;
                }
              }
              .cl-accordionContent {
                button {
                  color: ${props => props.theme.color.error[11]};
                  :focus {
                    box-shadow: none;
                  }
                }
              }
            }
          }
        }
        .cl-header {
          margin: 0px;
          h1 {
            color: ${props => props.theme.color.neutral[12]};
            font-size: 24px;
            font-weight: 700;
          }
          p {
            ${clerkThinFontCss}
            color: ${props => props.theme.color.neutral[11]};
          }
        }
        .cl-breadcrumbs {
          margin-bottom: ${props => props.theme.spacing[6]};
          .cl-breadcrumbsItems {
            a {
              color: ${props => props.theme.color.neutral[11]};
              ${clerkSmallThickFontCss}
              svg {
                display: none;
              }
            }
            p {
              color: ${props => props.theme.color.neutral[12]};
              ${clerkSmallThickFontCss}
              align-items: center;
              margin-bottom: 0px;
            }
          }
        }
        /* the following is selecting the right side of the profile settings modal,
        but it is not selecting the main page,
        rather the pages you can go into like when you click the 'Change password' button */
        :has(.cl-form),
        :has(p[data-localization-key='userProfile.backupCodePage.successMessage']),
        :has(.cl-qrCodeRow),
        :has(.cl-formFieldInputCopyToClipboardButton) {
          div {
            .cl-form {
              .cl-formFieldLabelRow__lastName, .cl-formField__newPassword, .cl-formField__confirmPassword  {
                margin-top: ${props => props.theme.spacing[5]};
              }
              .cl-avatarBox {
                display: none;
              }
              .cl-avatarBox + div {
                display: none;
              }
              .cl-formField__signOutOfOtherSessions {
                margin-top: ${props => props.theme.spacing[5]};
                input {
                  accent-color: ${props => props.theme.color.neutral[12]};
                }
              }
              .cl-formFieldLabelRow__signOutOfOtherSessions {
                margin-left: ${props => props.theme.spacing[2]};
              }
            }
            p {
              color: ${props => props.theme.color.neutral[12]};
              ${clerkThinFontCss}
              margin-bottom: ${props => props.theme.spacing[2]};
            }
          }
        }
        /* the following is selecting a div inside a page with the qr code,
        you can access it by clicking on 'Add two-step verification' button */
        div:has(.cl-qrCodeRow) {
          .cl-qrCodeContainer {
            padding: ${props => props.theme.spacing[2]};
          }
          div {
            div {
              gap: 0px;
              p {
                color: ${props => props.theme.color.neutral[12]};
                ${clerkThinFontCss}
                margin-bottom: 0px;
              }
              button[data-localization-key='userProfile.mfaTOTPPage.authenticatorApp.buttonUnableToScan__nonPrimary'] {
                margin-top: ${props => props.theme.spacing[4]};
                padding-left: ${props => props.theme.spacing[2]};
                color: ${props => props.theme.color.neutral[11]};
                ${clerkSmallThickFontCss}
                :focus {
                  box-shadow: none;
                }
              }
            }
          }
        }
        /* the following selection can be accessed by clicking on 'Add two-step verification' button,
        then clicking the 'Can't scan QR code?' button */
        div:has(.cl-formFieldInputCopyToClipboardButton) {
          div {
            div {
              gap: 0px;
              p[data-localization-key='userProfile.mfaTOTPPage.authenticatorApp.infoText__unableToScan'] {
                color: ${props => props.theme.color.neutral[12]};
                ${clerkThinFontCss}
              }
              p[data-localization-key='userProfile.mfaTOTPPage.authenticatorApp.inputLabel__unableToScan1'],
              p[data-localization-key='userProfile.mfaTOTPPage.authenticatorApp.inputLabel__unableToScan2'] {
                margin: 0px;
                color: ${props => props.theme.color.neutral[11]};
                ${clerkSmallFontCss}
              }
              div {
                margin-bottom: ${props => props.theme.spacing[4]};
                input {
                  background-color: ${props => props.theme.color.neutral[1]};
                  border: 1px solid ${props => props.theme.color.neutralAlpha[7]};
                  color: ${props => props.theme.color.neutral[12]};
                  ${clerkThinFontCss}
                  opacity: 1;
                }
              }
              button[data-localization-key='userProfile.mfaTOTPPage.authenticatorApp.buttonAbleToScan__nonPrimary'] {
                margin-top: ${props => props.theme.spacing[2]};
                padding-left: ${props => props.theme.spacing[2]};
                color: ${props => props.theme.color.neutral[11]};
                ${clerkSmallThickFontCss}
                :focus {
                  box-shadow: none;
                }
              }
            }
          }
        }
        /* the following selection can be accessed by clicking on 'Add two-step verification' button,
        then clicking the 'Continue' button */
        div:has(.cl-otpCodeField) {
          gap: 0px;
          .cl-form {
            margin-top: ${props => props.theme.spacing[5]};
            gap: 0px;
          }
          .cl-formHeaderTitle {
            color: ${props => props.theme.color.neutral[12]};
            margin-bottom: 0px;
            ${clerkBoldFontCss}
          }
          .cl-formHeaderSubtitle {
            color: ${props => props.theme.color.neutral[11]};
            margin-bottom: ${props => props.theme.spacing[4]};
            ${clerkThinFontCss}
          }
          .cl-otpCodeField {
            height: 48px;
            .cl-otpCodeFieldInputs {
              height: 48px;
              width: 328px;
              gap: ${props => props.theme.spacing[2]};
              input {
                height: 48px;
                width: 48px;
                max-width: unset;
                color: ${props => props.theme.color.neutral[12]};
              }
              .cl-otpCodeFieldInput {
                border-bottom: 2px solid ${props => props.theme.color.neutralAlpha[6]};
                :focus {
                  border-color: ${props => props.theme.color.neutral[12]};
                }
              }
            }
          }
        }
        /* the following selection can be accessed by a user with 2fa set,
        can be accessed by clicking on 'Regenerate codes' button under 'Backup codes' */
        .cl-page:has(p[data-localization-key='userProfile.backupCodePage.successMessage']) {
          div:has(p[data-localization-key='userProfile.backupCodePage.successMessage']) {
            gap: 0px;
            p[data-localization-key='userProfile.backupCodePage.successMessage'] {
              margin-bottom: ${props => props.theme.spacing[5]};
            }
            p[data-localization-key='userProfile.backupCodePage.title__codelist'] {
              ${clerkBoldFontCss}
              margin-bottom: 0px;
            }
            p[data-localization-key='userProfile.backupCodePage.subtitle__codelist'] {
              color: ${props => props.theme.color.neutral[11]};
              margin-bottom: 0px;
            }
            div:nth-child(3) {
              margin: ${props => props.theme.spacing[4]} 0px;
              gap: ${props => props.theme.spacing[2]};
              div {
                margin: 0px;
                padding: 2px ${props => props.theme.spacing[2]};
                border-radius: ${props => props.theme.radius[1]};
                background: ${props => props.theme.color.neutralAlpha[3]};
                p {
                  margin-bottom: 0px;
                  color: ${props => props.theme.color.neutralAlpha[11]};
                  ${clerkSmallThickFontCss}
                }
              }
            }
            div:has(button[data-localization-key='userProfile.backupCodePage.actionLabel__copy']) {
              gap: ${props => props.theme.spacing[4]};
            }
            button[data-localization-key='userProfile.backupCodePage.actionLabel__copy'],
            button[data-localization-key='userProfile.backupCodePage.actionLabel__copied'],
            button[data-localization-key='userProfile.backupCodePage.actionLabel__download'],
            button[data-localization-key='userProfile.backupCodePage.actionLabel__print'] {
              color: ${props => props.theme.color.neutral[11]};
              ${clerkSmallThickFontCss}
              :focus {
                box-shadow: none;
              }
            }
          }
        }
      }
    }

    .cl-badge {
      border-radius: 3px ${props => props.theme.radius[1]};
      background: ${props => props.theme.color.neutralAlpha[3]};
      display: flex;
      padding: 2px ${props => props.theme.spacing[2]};
      justify-content: center;
      align-items: center;
      color: ${props => props.theme.color.neutralAlpha[11]};
      ${clerkSmallThickFontCss}
    }

    .cl-formFieldInputCopyToClipboardButton {
      margin-top: 0px;
      svg {
        background-image: url(${require('~/assets/graphics/clipboard.svg')});
      }
      :focus {
        box-shadow: none;
      }
    }

    .cl-profilePage {
      #cl-section-account, #cl-section-security {
        margin: 0px;
      }
      gap: ${props => props.theme.spacing[5]};
    }

    .cl-profileSectionTitle {
      border-bottom-color: ${props => props.theme.color.neutralAlpha[4]};
    }

    .cl-profileSectionTitleText {
      margin-bottom: ${props => props.theme.spacing[2]};
      color: ${props => props.theme.color.neutral[12]};
      ${clerkBigFontCss}
    }

    .cl-profileSectionPrimaryButton {
      color: ${props => props.theme.color.neutral[11]};
      ${clerkBoldFontCss}
      border-radius: ${props => props.theme.radius.full};
      border: 1px solid transparent;
      height: 32px;
      padding: 0px ${props => props.theme.spacing[3]};
      gap: ${props => props.theme.spacing[2]};
      span {
        ${clerkBoldFontCss}
      }
      span {
        svg {
          height: 16px;
          width: 16px;
          path {
            display: none;
          }
        }
      }
    }

    .cl-headerTitle, .cl-profileSectionContent, .cl-internal-egu47 {
      justify-content: start;
    }

    .cl-headerTitle, .cl-headerSubtitle {
      text-align: start;
    }

    .cl-navbar ~ div[class^='cl-internal'] {
      display: none;
    }

    .cl-formButtonReset, .cl-formButtonPrimary {
      margin-top: ${props => props.theme.spacing[3]};
      ${clerkBoldFontCss}
      :focus {
        box-shadow: none;
      }
    }

    .cl-formButtonPrimary {
      ${clerkRadixGhostButtonSmallCss}
    }

    .cl-formButtonReset {
      ${clerkLightButtonSmallCss}
    }
  }

  p[data-localization-key='userProfile.profilePage.successMessage'] {
    color: ${props => props.theme.color.neutral[11]};
    ${clerkThinFontCss}
  }

  .cl-profileSection__emailAddresses, .cl-profileSection__connectedAccounts {
    display: none;
  }

  .ono-bar-chart {
    .highcharts-credits, .highcharts-legend {
      display: none;
    }

    .highcharts-point {
      fill: ${props => props.theme.color.accent[4]};
      stroke: ${props => props.theme.color.accent[9]};
      stroke-width: 2px;
      rx: ${props => props.theme.radius[1]};
    }

    .highcharts-legend-item .highcharts-point {
      rx: ${props => props.theme.radius[4]};
    }
  }

  .radix-themes.light .highcharts-background {
    fill: ${props => props.theme.white};
  }
  .radix-themes.dark .highcharts-background {
    fill: var(--color-panel-solid);
  }
  .highcharts-minor-grid-line {
    stroke: ${props => props.theme.color.neutralAlpha[6]};
  }
  .highcharts-grid-line {
    stroke: ${props => props.theme.color.neutralAlpha[6]};
  }
  .highcharts-axis-line {
    stroke: ${props => props.theme.color.neutralAlpha[6]};
  }
  .highcharts-tick {
    stroke: ${props => props.theme.color.neutralAlpha[6]};
  }
  .highcharts-label-box.highcharts-tooltip-box {
    fill: white;
    stroke: ${props => props.theme.color.neutral[6]};
    stroke-width: 1px;
  }
  #tooltip-download-csv {
    box-shadow: inset 0 0 0 1px ${colors.slateA.slateA7};
    color: ${colors.slate.slate11};
    &:hover {
      background: ${colors.slate.slate2};
    }

    .icon svg path {
      fill: ${colors.slate.slate11};
    }
  }
`;

export default GlobalStyles;
